/**
 * Unlock state and completion, derived from the save document.
 *
 * `PROG-053` is the rule this file exists to keep easy to see: unlock state is
 * determined from the save document alone, **never from the game module**.
 * Neither function below imports `@contracts/game-module` or anything shaped
 * like it -- `isLevelUnlocked` takes a `LevelIndex` (data already validated
 * out of a module by `platform/levels.ts`'s `validateGameIndex`) and a
 * `GameProgress` from the save document. That is what "without consulting the
 * game module" means in practice: the moment either function needed to call
 * `levelUrl` or `mount` to answer "is this unlocked", every future game would
 * have to answer through the same interface, and the shell would have stopped
 * being a shell. Checked by inspection, per the requirement's own
 * verification column -- there is no import here to point to a violation,
 * which is the property itself.
 *
 * No UI reads `isLevelUnlocked` yet. `SHELL-032` (level select shows locked
 * levels) is not in this sprint's stage list, and inventing a lock icon for a
 * screen that does not render one yet is the kind of unrequested scope this
 * project has already gone wrong on twice. `completionSummary` is used, by
 * `Picker.tsx`, for `SHELL-021`.
 *
 * `completionSummary` takes a level count rather than a `LevelIndex`, unlike
 * `isLevelUnlocked` above it: `Picker.tsx` (`SHELL-027`) renders without
 * loading any game module, so a real `LevelIndex` is exactly what it does not
 * have. The trade-off this accepts: a completed record for a level id that
 * has since stopped shipping cannot be told apart from a real one here, only
 * clamped so the total is never exceeded. `platform/game-registry.ts`'s
 * `levelCount` is what the caller actually holds, and levels are not expected
 * to be removed from a shipped game -- if that ever changes, this is the
 * function that needs the real index back.
 */

import type { LevelIndex, LevelIndexEntry } from '@contracts/level';
import type { GameProgress } from '@contracts/save';

/**
 * `DATA-012`: only `'adventure'` levels are ordered. A `'freeplay'` level has
 * no "previous" to have completed, so it is unlocked the moment it exists --
 * treating it as gated on a sequence that is not there would either lock it
 * forever or require inventing an order the schema deliberately does not
 * define for this mode.
 */
function orderedEntries(levelIndex: LevelIndex): LevelIndexEntry[] {
  return levelIndex.levels
    .filter((entry) => entry.mode === 'adventure')
    .slice()
    .sort((a, b) => a.index - b.index);
}

/**
 * `PROG-050`, `051`, `052`. The first level of a game -- of its ordered mode,
 * see `orderedEntries` -- is always unlocked; every other one unlocks when the
 * level immediately before it in manifest order is completed.
 *
 * Nothing here ever locks a level back up (`PROG-052`) because nothing here
 * remembers a previous answer to compare against -- completion in the save
 * document is monotonic (`PROG-044` never reduces a recorded result), so the
 * same derivation run twice against a save that has only gained completions
 * cannot produce a level that was unlocked and is not any more. Enforcing
 * `PROG-052` separately would be answering a question this design does not
 * have the shape to ask.
 */
export function isLevelUnlocked(
  levelIndex: LevelIndex,
  progress: GameProgress | undefined,
  levelId: string,
): boolean {
  if (levelIndex.levels.some((entry) => entry.id === levelId && entry.mode !== 'adventure')) {
    return true;
  }

  const ordered = orderedEntries(levelIndex);
  const position = ordered.findIndex((entry) => entry.id === levelId);
  if (position === -1) return false;
  if (position === 0) return true;

  const previous = ordered[position - 1];
  return Boolean(previous && progress?.levels[previous.id]?.completed);
}

export interface CompletionSummary {
  completed: number;
  total: number;
}

/**
 * `SHELL-021`. A count, not a fabricated fraction -- `0 of 2` says so
 * honestly rather than hiding a game nothing has been completed in yet.
 */
export function completionSummary(
  totalLevels: number,
  progress: GameProgress | undefined,
): CompletionSummary {
  if (!progress) return { completed: 0, total: totalLevels };
  const completed = Object.values(progress.levels).filter((record) => record.completed).length;
  return { completed: Math.min(completed, totalLevels), total: totalLevels };
}
